import { Node } from '@tiptap/core';
import type { NodeViewProps } from '@tiptap/core';
import type { FC } from 'react';
import { useChatResultData } from '../../hooks/hook';
import { useConversationContext } from '../../hooks/context';
import { Tooltip } from '../../../tooltip';
import { getSourceDisplayText } from '../../hooks/utils';
import { ReactNodeViewRenderer } from '@tiptap/react';

const Reference: FC<NodeViewProps> = ({ node }) => {
  const { onRenderBlockPreview } = useConversationContext();
  const refData = useChatResultData();

  if (!node?.attrs) return null;

  const { index } = node.attrs;

  const result = refData[index];

  if (!result) return null;

  const isShowPreview = result.type === 'block' && result.id && onRenderBlockPreview;

  return (
    <Tooltip
      className="inline "
      maxWidth={isShowPreview ? 400 : 320}
      popupClassName={'bg-white2 text-black !rounded-lg'}
      popup={
        result.id && isShowPreview ? (
          onRenderBlockPreview(result.id)
        ) : (
          <div className="p-2 space-y-2">
            <div className="text-t2-medium">{result?.title}</div>
            <div className="text-t3 line-clamp-6 text-grey3">{result?.content}</div>
            <div className="flex items-center gap-2 text-grey3">
              {result?.img_src && <img src={result.img_src} />}
              {result?.engine && <span>{getSourceDisplayText(result)}</span>}
            </div>
          </div>
        )
      }
    >
      <a
        href={result.url}
        target="_blank"
        className="inline-flex items-center justify-center w-5 h-5 bg-grey6 rounded-full text-grey1 text-t4 mx-1 ignore-security-dialog"
        rel="noreferrer"
      >
        {index + 1}
      </a>
    </Tooltip>
  );
};

export const ReferenceExtension = Node.create({
  name: 'reference',
  group: 'inline',
  inline: true,
  atom: true,

  addAttributes() {
    return {
      index: {
        default: 0,
        parseHTML: (element) => {
          const value = element.getAttribute('data-index');
          return value ? parseInt(value, 10) : 0;
        },
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'span[data-type="reference"][data-index]',
      },
    ];
  },

  renderHTML({ node }) {
    return [
      'span',
      {
        'data-type': 'reference',
        'data-index': node.attrs.index,
      },
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(Reference);
  },
});
